import React, { useState } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Card, Popconfirm, Tooltip, message, Typography, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Profile from 'utils/apiHelpers/profilehelper.ts';
import { removeProfile } from 'redux/reducers/profiles';
import { wsSendDeletedProfileId } from 'redux/middlewares/signalr';
import getProfileTypeLogoUrl from 'utils/getProfileTypeLogoUrl';
import { profileTypeSelector } from 'redux/reducers/profileTypes';
import './styles.less';

const { Text } = Typography;

const EditButton = ({ editable, handleClick }) => {
  const { t } = useTranslation();

  if (editable) {
    return <EditOutlined key="edit" onClick={handleClick} />;
  }

  return (
    <Tooltip placement="bottom" title={t('profiles.noEditableData')}>
      <EditOutlined className="profile-card__edit-button--disabled" key="edit" />
    </Tooltip>
  );
};

EditButton.propTypes = {
  editable: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

const ProfileCard = ({ profile, onEdit }) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profileType = useSelector((s) => profileTypeSelector(s, profile.profileTypeId));

  const handleRemoveProfile = async () => {
    setDeleteLoading(true);
    await Profile.delete(profile.profileId);
    setDeleteLoading(false);

    dispatch(removeProfile(profile.profileId));
    dispatch(wsSendDeletedProfileId(profile.profileId));
    message.success(t('profiles.profileDeleted', { provider: profile.profileName }));
  };

  const handleEditClick = () => {
    if (!deleteLoading) {
      onEdit(profile);
    }
  };

  return (
    <Card
      className="profile-card"
      actions={[
        <EditButton
          editable={!!profileType && Object.keys(profileType.editableFields).length > 0}
          handleClick={handleEditClick}
        />,
        <Popconfirm
          onConfirm={handleRemoveProfile}
          placement="topRight"
          title={t('profiles.deleteThisProfile')}
          arrowPointAtCenter
          disabled={deleteLoading}
        >
          <DeleteOutlined key="delete" />
        </Popconfirm>,
      ]}
      cover={
        <div className="profile-card__cover">
          <img
            className="profile-card__cover__img"
            src={getProfileTypeLogoUrl(profile.profileTypeId)}
            alt={profile.profileTypeId}
            title={profile.profileTypeId}
          />
        </div>
      }
    >
      <div className="profile-card-body" title={profile.profileName}>
        <div className="profile-card-body__line">
          <span className="profile-card-body__label">
            <Text type="secondary">Service:</Text>
          </span>{' '}
          {profile.profileName}
        </div>
        <div className="profile-card-body__line">
          <span className="profile-card-body__label">
            <Text type="secondary">{t('profiles.status')}:</Text>
          </span>{' '}
          <Tag color={profile.isActive ? 'green' : 'red'}>
            {profile.isActive ? t('profiles.statusActive') : t('profiles.statusInactive')}
          </Tag>
        </div>
      </div>
    </Card>
  );
};

ProfileCard.propTypes = {
  profile: PropTypes.shape({
    profileId: PropTypes.number,
    profileTypeId: PropTypes.string,
    profileName: PropTypes.string,
    isActive: PropTypes.bool,
  }).isRequired,

  onEdit: PropTypes.func,
};

ProfileCard.defaultProps = {
  onEdit: () => {},
};

export default ProfileCard;
